/**
 * Lectura ESTRICTA de las respuestas del backend.
 *
 * POR QUÉ EXISTE ESTE MÓDULO. Estas comprobaciones nacieron dentro de
 * `disponibilidad.ts`, cuando esa era la única ruta con contrato escrito. Ya no
 * lo es: `/api/reservaciones`, `/api/reservaciones/:id/cancelar` y
 * `/api/mis-reservaciones` también lo tienen. Tener dos copias de las mismas
 * comprobaciones garantiza que algún día divergan, y una divergencia aquí no se
 * ve como un error: se ve como una pantalla con un hueco.
 *
 * LA DIFERENCIA CON `cliente.ts`. Ahí la lectura es tolerante —acepta
 * `snake_case` y `camelCase`, y varias formas de envoltura— porque se escribió
 * cuando el contrato no existía. Aquí NO. Cada campo se exige con su nombre y
 * su tipo exactos, y si no cumple se lanza un error que dice QUÉ campo falló.
 * Rellenar con un valor plausible convertiría un hueco del backend en una
 * reservación que no existe.
 *
 * Los nombres de las respuestas son `snake_case`; los de este código son
 * `camelCase`. La correspondencia se anota campo por campo en quien llama.
 */

import { esRegistro } from './errores'

/** Se lanza cuando la respuesta no cumple el contrato. */
export class ErrorDeContrato extends Error {
  constructor(mensaje: string) {
    super(`La respuesta del backend no cumple el contrato: ${mensaje}`)
    this.name = 'ErrorDeContrato'
  }
}

export function exigirRegistro(
  valor: unknown,
  ruta: string,
): Record<string, unknown> {
  if (!esRegistro(valor)) {
    throw new ErrorDeContrato(`se esperaba un objeto en ${ruta}.`)
  }
  return valor
}

export function exigirTexto(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): string {
  const valor = registro[clave]
  if (typeof valor !== 'string') {
    throw new ErrorDeContrato(`${ruta}.${clave} debe ser texto.`)
  }
  return valor
}

export function exigirTextoONulo(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): string | null {
  const valor = registro[clave]
  if (valor === null) return null
  if (typeof valor !== 'string') {
    throw new ErrorDeContrato(`${ruta}.${clave} debe ser texto o null.`)
  }
  return valor
}

export function exigirBooleano(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): boolean {
  const valor = registro[clave]
  if (typeof valor !== 'boolean') {
    throw new ErrorDeContrato(`${ruta}.${clave} debe ser booleano.`)
  }
  return valor
}

/** Conteos: entero y nunca negativo. */
export function exigirEntero(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): number {
  const valor = registro[clave]
  if (typeof valor !== 'number' || !Number.isInteger(valor) || valor < 0) {
    throw new ErrorDeContrato(`${ruta}.${clave} debe ser un entero no negativo.`)
  }
  return valor
}

/**
 * Enteros que empiezan en 1. El caso real es `lugar`, que en la base lleva
 * `CHECK (lugar >= 1)` desde la migración 001: un lugar 0 sería un dato roto,
 * no un lugar.
 */
export function exigirEnteroPositivo(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): number {
  const valor = registro[clave]
  if (typeof valor !== 'number' || !Number.isInteger(valor) || valor < 1) {
    throw new ErrorDeContrato(
      `${ruta}.${clave} debe ser un entero mayor o igual a 1.`,
    )
  }
  return valor
}

/**
 * El identificador de una fila `bigserial`, normalizado a texto.
 *
 * HUECO DEL CONTRATO, DECLARADO. `ocupaciones.id` y `custodia_llave.id` son
 * `bigserial`, y JSON no tiene un tipo entero de 64 bits: un `bigint` se puede
 * serializar como número o como texto, y las dos formas son correctas. Hoy
 * `packages/db` instala un `typeParser` para el OID 20 que lo convierte a
 * `Number`, así que llegará como número; pero un serializador que lo mande como
 * texto —para no perder precisión— no estaría incumpliendo nada.
 *
 * Aceptar las dos NO es lectura tolerante: no se adivinan nombres ni formas, se
 * reconoce que el contrato no fija la codificación de este campo. Se normaliza
 * a texto porque el id solo se usa como identificador opaco —arma la URL de la
 * operación y sirve de clave de lista—, y así una precisión perdida en el camino
 * no puede convertirse en actuar sobre la fila equivocada.
 *
 * Vive aquí y no en `reservaciones.ts`, donde nació, porque ya son dos los
 * módulos que lo necesitan: el otro es `mostrador.ts`.
 */
export function exigirIdentificador(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): string {
  const valor = leerIdentificador(registro[clave])
  if (valor === null) {
    throw new ErrorDeContrato(
      `${ruta}.${clave} debe ser un identificador, como entero positivo o como texto.`,
    )
  }
  return valor
}

/**
 * El mismo identificador, cuando el contrato admite que NO haya.
 *
 * El caso real es el tablero del mostrador: sus tres columnas viajan con la
 * misma forma de fila, y `ocupacion` solo existe en «por llegar» mientras que
 * `custodia` solo existe en «llave fuera». Un `null` ahí no es un hueco de
 * datos: es que ese campo no aplica a esa columna.
 */
export function exigirIdentificadorONulo(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): string | null {
  const valor = registro[clave]
  if (valor === null || valor === undefined) return null
  const identificador = leerIdentificador(valor)
  if (identificador === null) {
    throw new ErrorDeContrato(
      `${ruta}.${clave} debe ser un identificador —entero positivo o texto— o null.`,
    )
  }
  return identificador
}

function leerIdentificador(valor: unknown): string | null {
  if (typeof valor === 'string' && valor.trim() !== '') return valor
  if (typeof valor === 'number' && Number.isInteger(valor) && valor > 0) {
    return String(valor)
  }
  return null
}

/** `exigirEnteroPositivo` donde el contrato admite que el campo no aplique. */
export function exigirEnteroPositivoONulo(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): number | null {
  const valor = registro[clave]
  if (valor === null || valor === undefined) return null
  return exigirEnteroPositivo(registro, clave, ruta)
}

export function exigirOpcion<T extends string>(
  registro: Record<string, unknown>,
  clave: string,
  opciones: readonly T[],
  ruta: string,
): T {
  const valor = registro[clave]
  if (
    typeof valor !== 'string' ||
    !(opciones as readonly string[]).includes(valor)
  ) {
    throw new ErrorDeContrato(
      `${ruta}.${clave} debe ser uno de: ${opciones.join(', ')}.`,
    )
  }
  return valor as T
}

export function exigirLista(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): unknown[] {
  const valor = registro[clave]
  if (!Array.isArray(valor)) {
    throw new ErrorDeContrato(`${ruta}.${clave} debe ser una lista.`)
  }
  return valor
}

/**
 * Instante UTC en ISO 8601. Se convierte aquí para no volver a parsear en la
 * vista, y para que un instante inválido salte en el cliente del API y no
 * mientras se pinta una hora.
 */
export function exigirInstante(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): Date {
  const valor = exigirTexto(registro, clave, ruta)
  const milisegundos = Date.parse(valor)
  if (Number.isNaN(milisegundos)) {
    throw new ErrorDeContrato(
      `${ruta}.${clave} no es un instante ISO 8601 válido («${valor}»).`,
    )
  }
  return new Date(milisegundos)
}

/** `exigirInstante` donde el contrato admite que el campo no aplique. */
export function exigirInstanteONulo(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): Date | null {
  const valor = registro[clave]
  if (valor === null || valor === undefined) return null
  return exigirInstante(registro, clave, ruta)
}

/**
 * La zona horaria no se supone ni se escribe en el código: viaja en la
 * respuesta porque vive en la tabla `configuracion`. Se verifica que el motor
 * de fechas la acepte, porque si no, todas las horas de la pantalla saldrían
 * mal sin que nada avisara.
 */
export function exigirZonaHoraria(
  registro: Record<string, unknown>,
  clave: string,
  ruta: string,
): string {
  const valor = exigirTexto(registro, clave, ruta)
  if (!esZonaHorariaReconocida(valor)) {
    throw new ErrorDeContrato(
      `${ruta}.${clave} no es una zona horaria reconocida («${valor}»).`,
    )
  }
  return valor
}

/**
 * `true` si `Intl` sabe formatear en esa zona. Se exporta suelto porque
 * `/api/configuracion` entrega `zona_horaria` fuera de una lectura de contrato,
 * y ahí también hay que comprobarla antes de formatear una hora con ella.
 */
export function esZonaHorariaReconocida(valor: string): boolean {
  try {
    new Intl.DateTimeFormat('es-MX', { timeZone: valor })
    return true
  } catch {
    return false
  }
}
